import React, { useCallback, useEffect, useState } from "react";
import {
  CButton,
  CFormInput,
  CFormLabel,
  CFormText,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import type { ConfirmModalData } from "@src/services/state/layoutReducer";

interface Props {
  data: ConfirmModalData;
  onConfirm: (confirmed: boolean, promptValue?: string) => void;
  onCancel?: () => void;
}

/** Non-blocking confirmation modal. Supports optional prompt input via `data.promptConfig`. */
export const ConfirmModal: React.FC<Props> = ({ data, onConfirm, onCancel }) => {
  const { hideModal } = useConfirmModal();
  const [promptValue, setPromptValue] = useState(data.promptConfig?.defaultValue || "");

  // Reset prompt value when modal config changes (modal reuse without unmount)
  useEffect(() => {
    setPromptValue(data.promptConfig?.defaultValue || "");
  }, [data.promptConfig?.defaultValue]);

  const handleConfirm = useCallback(() => {
    onConfirm(true, data.promptConfig ? promptValue : undefined);
    hideModal();
  }, [onConfirm, hideModal, promptValue, data.promptConfig]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      onConfirm(false);
    }
    hideModal();
  }, [onConfirm, onCancel, hideModal]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm],
  );

  return (
    <CModal
      className="ConfirmModal"
      visible={true}
      onClose={handleCancel}
      backdrop="static"
      keyboard={true}
    >
      <CModalHeader closeButton>
        <CModalTitle>{data.title || "Confirm"}</CModalTitle>
      </CModalHeader>

      <CModalBody>
        <p style={{ whiteSpace: "pre-wrap", margin: data.promptConfig ? "0 0 1rem 0" : 0 }}>
          {data.message}
        </p>

        {data.promptConfig && (
          <div>
            {data.promptConfig.label && (
              <CFormLabel htmlFor="confirm-modal-prompt">{data.promptConfig.label}</CFormLabel>
            )}
            <CFormInput
              id="confirm-modal-prompt"
              type={data.promptConfig.type || "text"}
              placeholder={data.promptConfig.placeholder}
              value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            {data.promptConfig.helpText && (
              <CFormText className="text-muted">{data.promptConfig.helpText}</CFormText>
            )}
          </div>
        )}
      </CModalBody>

      <CModalFooter>
        <CButton color="secondary" onClick={handleCancel}>
          {data.cancelLabel || "Cancel"}
        </CButton>
        <CButton color={data.confirmColor || "danger"} onClick={handleConfirm}>
          {data.confirmLabel || "Confirm"}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};
